/**
 * TrustFrame public theme hook
 * ============================
 * Mounts the visitor's color-scheme preference on `<html data-tf-mode>` so
 * every Tailwind `tf.*` token (defined as CSS variables in index.css) flips
 * between cream + IKB and dark IKB + orange.
 *
 *  - localStorage `tf_public_theme` wins (explicit user choice)
 *  - otherwise `prefers-color-scheme: light` is honoured
 *  - live OS theme changes are followed until the user toggles
 *  - cleanup on unmount so admin /dashboard (which does not mount this hook)
 *    keeps the default light palette
 */
import { useState, useEffect, useCallback } from 'react';

const KEY = 'tf_public_theme';

function read() {
  // localStorage can throw (Safari private mode, sandboxed iframe, blocked storage);
  // matchMedia can throw on older browsers. Both branches degrade silently to defaults.
  try {
    const v = window.localStorage.getItem(KEY);
    if (v === 'light' || v === 'dark') return v;
  } catch (_e) {
    // storage unavailable — fall through to OS preference
  }
  try {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: light)').matches) {
      return 'light';
    }
  } catch (_e) {
    // matchMedia unavailable — fall through to default
  }
  return 'dark';
}

function write(m) {
  try {
    window.localStorage.setItem(KEY, m);
  } catch (_e) {
    // storage unavailable — preference simply won't persist
  }
}

export default function usePublicTheme() {
  const [mode, setMode] = useState(read);

  const toggle = useCallback(() => {
    setMode((m) => {
      const next = m === 'dark' ? 'light' : 'dark';
      write(next);
      return next;
    });
  }, []);

  // Apply attribute on <html>; clean up on unmount so non-public routes (admin)
  // see the default light variable set.
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-tf-mode', mode);
    return () => {
      root.removeAttribute('data-tf-mode');
    };
  }, [mode]);

  // Follow live OS changes only when no explicit choice has been made.
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const handler = (e) => {
      let stored = null;
      try {
        stored = window.localStorage.getItem(KEY);
      } catch {
        /* ignore */
      }
      if (stored !== 'light' && stored !== 'dark') {
        setMode(e.matches ? 'light' : 'dark');
      }
    };
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else if (mq.addListener) mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else if (mq.removeListener) mq.removeListener(handler);
    };
  }, []);

  return [mode, toggle];
}
